import { BaseModel } from '@/models/BaseModel';
import {
  AdvancedAddonsStyleData,
  AdvancedElementData,
  AdvancedStyleData,
  PositionData,
  TransitionsData
} from '@/typings/interfaces/data/settings/settings';
import useDevice, { getDeviceData } from '@/hooks/useDevice';
import { TransitionTypes } from '@/typings/types/types';
import {
  AdvancedHorizontalAlignmentType,
  AdvancedPositionType,
  AdvancedVerticalAlignmentType,
  AdvancedWidthType
} from '@/typings/enums/enums';
import { CSSProperties } from 'vue';

interface AddonAdvancedElementState {
  zIndex?: number;
  elementId?: string;
  className?: string;
}

interface AddonAdvancedGenericState {
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  padding?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  width?: {
    type?: AdvancedWidthType;
    custom?: string;
  };
  minHeight?: string;
  horizontalAlignment?: string;
}

interface AddonAdvancedPositionState {
  position?: AdvancedPositionType;
  horizontal?: {
    offset: string;
    alignment?: AdvancedHorizontalAlignmentType;
  };
  vertical?: {
    offset: string;
    alignment?: AdvancedVerticalAlignmentType;
  };
}

interface AddonAdvancedTransitionsState {
  enabled: boolean;
  type?: TransitionTypes;
  duration?: number;
  delay?: number;
}

export interface AddonsAdvancedSettingsState {
  generic?: AddonAdvancedGenericState;
  advanced?: AddonAdvancedElementState;
  position?: AddonAdvancedPositionState;
  transitions?: AddonAdvancedTransitionsState;
  elementStyling?: {
    general: CSSProperties;
  };
  styling?: CSSProperties;
}

export class AddonsAdvancedSettingsModel extends BaseModel<AdvancedAddonsStyleData, AddonsAdvancedSettingsState> {
  parse(data: AdvancedAddonsStyleData) {
    const state = this.state;

    // Create empty objects and set values
    if (data?.generic) {
      state.generic = AddonsAdvancedSettingsModel.parseGenericData(data);
    } else {
      state.generic = undefined;
    }
    if (data?.advanced) {
      state.advanced = AddonsAdvancedSettingsModel.parseAdvancedData(data);
    } else {
      state.advanced = undefined;
    }
    if (data?.position) {
      state.position = AddonsAdvancedSettingsModel.parsePositionData(data);
    } else {
      state.position = undefined;
    }
    if (data?.transitions) {
      state.transitions = AddonsAdvancedSettingsModel.parseTransitionData(data);
    } else {
      state.transitions = undefined;
    }

    state.styling = state.styling || {};

    state.styling = AddonsAdvancedSettingsModel.constructElementInlineStyles(state);
  }

  private static parseGenericData(data: AdvancedAddonsStyleData): AddonAdvancedGenericState | undefined {
    if (data?.generic) {
      const useData = getDeviceData(data.generic) as AdvancedStyleData | undefined;

      if (!useData) {
        return undefined;
      }
      return AddonsAdvancedSettingsModel.constructGenericState(useData);
    }
  }

  private static constructGenericState(data: AdvancedStyleData): AddonAdvancedGenericState {
    return {
      margin: {
        ...(data.margin?.top && { top: Number(data.margin?.top) }),
        ...(data.margin?.right && {
          right: Number(data.margin?.right)
        }),
        ...(data.margin?.bottom && {
          bottom: Number(data.margin?.bottom)
        }),
        ...(data.margin?.left && { left: Number(data.margin?.left) })
      },
      padding: {
        ...((data.padding?.top || data?.padding?.top === 0) && { top: Number(data.padding?.top) }),
        ...((data.padding?.right || data?.padding?.right === 0) && {
          right: Number(data.padding?.right)
        }),
        ...((data.padding?.bottom || data?.padding?.bottom === 0) && {
          bottom: Number(data.padding?.bottom)
        }),
        ...((data.padding?.left || data?.padding?.bottom === 0) && {
          left: Number(data.padding?.left)
        })
      },

      width: {
        ...(data.width?.type && { type: data.width?.type }),
        ...(data.width?.custom && { custom: data.width?.custom })
      },
      ...(data.min_height && { minHeight: data.min_height }),
      ...(data.horisontal_alignment && {
        horizontalAlignment: data.horisontal_alignment
      })
    };
  }

  private static parseAdvancedData(data: AdvancedAddonsStyleData): AddonAdvancedElementState | undefined {
    if (data.advanced) {
      return AddonsAdvancedSettingsModel.constructAdvancedState(data.advanced);
    }
    return undefined;
  }

  private static constructAdvancedState(data: AdvancedElementData): AddonAdvancedElementState {
    return {
      ...(data.zindex && { zIndex: Number(data.zindex) }),
      ...(data.element_id && { elementId: data.element_id }),
      ...(data.classname && { className: data.classname })
    };
  }

  private static parsePositionData(data: AdvancedAddonsStyleData): AddonAdvancedPositionState | undefined {
    if (data?.position) {
      const useData = this.getPositionDeviceData(data);

      if (!useData) {
        return undefined;
      }
      return AddonsAdvancedSettingsModel.constructPositionState(useData);
    }
  }

  public static getPositionDeviceData(data: AdvancedAddonsStyleData): PositionData | undefined {
    const desktopData = data.position?.desktop;

    const { isMobile, isTablet } = useDevice();

    let overwriteMobile = undefined;
    let overwriteTablet = undefined;
    if (desktopData) {
      overwriteMobile =
        typeof desktopData['overwrite_mobile'] === 'undefined' || desktopData['overwrite_mobile'] === '1';

      overwriteTablet =
        typeof desktopData['overwrite_tablet'] === 'undefined' || desktopData['overwrite_tablet'] === '1';
    }

    if (isMobile) {
      if (!overwriteMobile) {
        return data.position?.mobile ? data.position?.mobile : desktopData;
      }
      return desktopData;
    }

    if (isTablet) {
      if (!overwriteTablet) {
        return data.position?.tablet ? data.position?.tablet : desktopData;
      }
      return desktopData;
    }

    return desktopData;
  }

  private static constructPositionState(data: PositionData): AddonAdvancedPositionState {
    let horizontal = data.horizontal;
    let vertical = data.vertical;

    if (
      (data.position && data.position === AdvancedPositionType.FIXED) ||
      data.position === AdvancedPositionType.ABSOLUTE
    ) {
      if (!horizontal?.offset) {
        horizontal = {
          alignment: AdvancedHorizontalAlignmentType.LEFT,
          offset: '0px'
        };
      }

      if (!vertical?.offset) {
        vertical = {
          alignment: AdvancedVerticalAlignmentType.TOP,
          offset: '0px'
        };
      }
    }

    return {
      ...(data.position && { position: data.position }),
      ...(horizontal && { horizontal }),
      ...(vertical && { vertical })
    };
  }

  private static parseTransitionData(data: AdvancedAddonsStyleData): AddonAdvancedTransitionsState | undefined {
    if (data?.transitions) {
      const useData = getDeviceData(data.transitions) as TransitionsData | undefined;

      if (!useData) {
        return undefined;
      }
      return AddonsAdvancedSettingsModel.constructTransitionState(useData);
    }
  }

  private static constructTransitionState(data: TransitionsData): AddonAdvancedTransitionsState {
    const transitions: AddonAdvancedTransitionsState = {
      enabled: false
    };

    if (data.enter && data.enter !== 'none') {
      transitions.enabled = true;
      transitions.type = data.enter;
      if (data.enter_delay) {
        transitions.delay = Number(data.enter_delay);
      }
      if (data.enter_duration) {
        transitions.duration = Number(data.enter_duration);
      }
    }

    return transitions;
  }

  private static constructElementInlineStyles(state: AddonsAdvancedSettingsState): CSSProperties | undefined {
    if (!state) return;

    const genericStyle = state.generic;
    const marginStyle = genericStyle?.margin;
    const paddingStyle = genericStyle?.padding;
    const advancedStyle = state.advanced;
    const positionStyle = state.position;

    let alignSelfValue = 'flex-start';
    switch (genericStyle?.horizontalAlignment) {
      case AdvancedHorizontalAlignmentType.LEFT:
        alignSelfValue = 'flex-start';
        break;
      case AdvancedHorizontalAlignmentType.RIGHT:
        alignSelfValue = 'flex-end';
        break;

      case AdvancedHorizontalAlignmentType.CENTER:
        alignSelfValue = 'center';
        break;
      default:
    }

    return {
      ...(typeof marginStyle?.top !== 'undefined' && { marginTop: `${marginStyle?.top}px` }),
      ...(typeof marginStyle?.bottom !== 'undefined' && { marginBottom: `${marginStyle?.bottom}px` }),
      ...(marginStyle?.right && {
        marginRight: `${marginStyle.right}px`
      }),
      ...(marginStyle?.left && {
        marginLeft: `${marginStyle.left}px`
      }),

      ...(paddingStyle?.top && {
        paddingTop: `${paddingStyle.top}px`
      }),
      ...(paddingStyle?.bottom && {
        paddingBottom: `${paddingStyle?.bottom}px`
      }),
      ...(paddingStyle?.right && {
        paddingRight: `${paddingStyle.right}px`
      }),
      ...(paddingStyle?.left && {
        paddingLeft: `${paddingStyle.left}px`
      }),

      ...(positionStyle?.position && {
        position: positionStyle?.position,

        ...(positionStyle?.vertical &&
          (positionStyle.position === AdvancedPositionType.ABSOLUTE ||
            positionStyle.position === AdvancedPositionType.FIXED) && {
            ...(positionStyle.vertical.alignment !== AdvancedVerticalAlignmentType.BOTTOM && {
              top: positionStyle.vertical?.offset ? positionStyle.vertical.offset : 0,
              bottom: 'auto'
            }),
            ...(positionStyle.vertical.alignment === AdvancedVerticalAlignmentType.BOTTOM &&
              (positionStyle.position === AdvancedPositionType.ABSOLUTE ||
                positionStyle.position === AdvancedPositionType.FIXED) && {
                top: 'auto',
                bottom: positionStyle.vertical.offset ? positionStyle.vertical.offset : 0
              })
          }),
        ...(positionStyle?.horizontal && {
          ...(positionStyle.horizontal?.alignment !== AdvancedHorizontalAlignmentType.RIGHT &&
            (positionStyle.position === AdvancedPositionType.ABSOLUTE ||
              positionStyle.position === AdvancedPositionType.FIXED) && {
              left: positionStyle.horizontal?.offset ? positionStyle.horizontal.offset : 0,
              right: 'auto'
            }),
          ...(positionStyle.horizontal?.alignment === AdvancedHorizontalAlignmentType.RIGHT &&
            (positionStyle.position === AdvancedPositionType.ABSOLUTE ||
              positionStyle.position === AdvancedPositionType.FIXED) && {
              left: 'auto',
              right: positionStyle.horizontal?.offset ? positionStyle.horizontal.offset : 0
            })
        })
      }),

      ...(genericStyle?.minHeight && {
        minHeight: genericStyle.minHeight
      }),

      ...(genericStyle?.horizontalAlignment && {
        alignSelf: alignSelfValue
      }),

      ...(advancedStyle?.zIndex && {
        zIndex: advancedStyle.zIndex,
        position: 'relative'
      }),
      ...(genericStyle?.width?.type === AdvancedWidthType.CUSTOM && {
        width: genericStyle.width.custom
      })
    };
  }

  authorSignature(): string {
    return 'Nicky Christensen | Sebastian Jakobsen';
  }
}
